// ── IndustryBenchmark — 외식업 평균 vs 본인 매장 비교 카드 ──
// 4 KPI를 양방향 막대로 표시 (왼쪽 = 평균, 오른쪽 = 본인)
import React, { useMemo } from 'react';
import {
  INDUSTRY_BENCHMARKS, compareToBenchmark, calcUserMetrics, STATUS_COLORS,
} from '../utils/industryBenchmark';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const INDICATOR_CONFIGS = [
  { key: 'netMargin',     icon: '📈', shortLabel: '순이익률' },
  { key: 'foodCost',      icon: '🥬', shortLabel: '식재료 원가율' },
  { key: 'laborCost',     icon: '👥', shortLabel: '인건비 비율' },
  { key: 'revenueGrowth', icon: '🚀', shortLabel: '매출 성장률' },
];

export default function IndustryBenchmark({ profitLoss, monthKey, prevMonthKey, isMobile }) {
  const userMetrics = useMemo(
    () => calcUserMetrics({ profitLoss, monthKey, prevMonthKey }),
    [profitLoss, monthKey, prevMonthKey]
  );
  const ind = INDUSTRY_BENCHMARKS.food_service.indicators;

  // 입력 가능 데이터 카운트
  const evaluableCount = INDICATOR_CONFIGS.filter(c => userMetrics[c.key] !== null).length;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: isMobile ? '16px 14px' : '18px 22px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
    }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
          📊 외식업 평균 vs 우리 매장
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
          {evaluableCount === 0
            ? '데이터를 입력하면 평균 대비 위치가 보여요'
            : `${evaluableCount}/4개 지표 비교 중`}
        </div>
        {userMetrics._projected && (
          <div style={{
            fontSize: 11, color: '#18181B', marginTop: 6,
            padding: '4px 8px', background: "#FAFAFA", borderRadius: 6,
            display: 'inline-block',
          }}>
            📈 {userMetrics._todayDay}일까지 매출 추세로 월말 ({userMetrics._daysInMonth}일) 예상 기준 비교
          </div>
        )}
      </div>

      {evaluableCount === 0 ? (
        <div style={{
          textAlign: 'center', padding: '22px 14px',
          color: '#9ca3af', fontSize: 12,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📈</div>
          매출·비용을 입력하면 외식업 평균과 비교한 결과가 나타나요
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {INDICATOR_CONFIGS.map(cfg => {
            const actual = userMetrics[cfg.key];
            const indDef = ind[cfg.key];
            const cmp = compareToBenchmark(cfg.key, actual);
            return (
              <BenchmarkRow key={cfg.key}
                cfg={cfg} indDef={indDef} actual={actual} cmp={cmp}
                isMobile={isMobile} />
            );
          })}
        </div>
      )}

      {/* 출처 */}
      <div style={{
        marginTop: 12, paddingTop: 10,
        borderTop: '1px dashed #e5e7eb',
        fontSize: 10, color: '#9ca3af', textAlign: 'center', lineHeight: 1.6,
      }}>
        📚 출처: 한국외식산업연구원 · 통계청 서비스업동향조사 · 소상공인시장진흥공단 (2024~2025) · 업종/규모/지역 따라 편차 있음
      </div>
    </div>
  );
}

// ── 한 줄 비교 ──
// 디자인 정책: 비교 막대 제거, 숫자/라벨 중심.
//   - 카드 배경/보더는 옅은 그레이로 통일 (status 색상 보더·BG 제거)
//   - 상태 배지(우수/평균 초과 등)만 컬러 유지
function BenchmarkRow({ cfg, indDef, actual, cmp, isMobile }) {
  const status = STATUS_COLORS[cmp.status];

  if (actual === null) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        background: '#fff',
        border: '1px solid #F4F4F5',
        borderRadius: 10,
        opacity: 0.7,
      }}>
        <span style={{ fontSize: 16 }}>{cfg.icon}</span>
        <span style={{ flex: 1, fontSize: 13, color: '#71717A', fontWeight: 600 }}>
          {cfg.shortLabel}
        </span>
        <span style={{ fontSize: 11, color: '#A1A1AA' }}>
          평균 {indDef.avg}{indDef.unit}
        </span>
        <span style={{ fontSize: 11, color: '#A1A1AA', fontStyle: 'italic' }}>
          데이터 없음
        </span>
      </div>
    );
  }

  // 차이 텍스트 (1줄로 통합)
  const deltaText = cmp.delta !== null && Math.abs(cmp.delta) >= 0.5
    ? (cmp.direction === 'higher'
        ? (cmp.delta >= 0
            ? `평균보다 +${cmp.delta.toFixed(1)}${indDef.unit} 높음`
            : `평균보다 ${cmp.delta.toFixed(1)}${indDef.unit} 낮음`)
        : (cmp.delta <= 0
            ? `평균보다 ${cmp.delta.toFixed(1)}${indDef.unit} 낮음 (좋음)`
            : `평균보다 +${cmp.delta.toFixed(1)}${indDef.unit} 높음 (개선 필요)`))
    : null;

  return (
    <div style={{
      padding: '12px 14px',
      background: '#fff',
      border: '1px solid #F4F4F5',
      borderRadius: 10,
    }}>
      {/* 1행: 아이콘 + 라벨 + 평균 vs 우리 + 상태 배지 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>{cfg.icon}</span>
        <span style={{
          fontSize: 13, fontWeight: 700, color: '#18181B',
          minWidth: isMobile ? 80 : 100,
        }}>
          {cfg.shortLabel}
        </span>
        <div style={{ flex: 1, display: 'flex', gap: 10, fontSize: 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#71717A' }}>
            업계 <strong style={{ color: '#18181B', marginLeft: 2 }}>{indDef.avg}{indDef.unit}</strong>
          </span>
          <span style={{ color: '#A1A1AA' }}>vs</span>
          <span style={{ color: '#71717A' }}>
            우리 <strong style={{ color: status.text, fontSize: 13, marginLeft: 2 }}>
              {actual >= 0 && cfg.key === 'revenueGrowth' ? '+' : ''}
              {actual.toFixed(1)}{indDef.unit}
            </strong>
          </span>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 800,
          color: status.text,
          padding: '3px 10px', borderRadius: 12,
          background: status.bg,
          whiteSpace: 'nowrap',
        }}>
          {cmp.label}
        </span>
      </div>

      {/* 2행: 차이 텍스트 (있을 때만) */}
      {deltaText && (
        <div style={{
          fontSize: 11, color: '#71717A', marginTop: 6, marginLeft: 26,
        }}>
          {deltaText}
        </div>
      )}
    </div>
  );
}
